import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Clock, FileText, Search, Package as PackageIcon, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import { DeliveryRequestApi, CourierPackageApi, LocationApi, DeliveryConfigApi, TownApi, ParishApi, DeliveryFeeApi } from '@shared/sdk-v1/apis';
import { DeliveryRequest as DeliveryRequestModel, CourierPackage, Location, NewDeliveryRequest, DeliveryConfig, DeliveryFee, Parish } from '@shared/sdk-v1/models';
import { apiConfig } from '../shared/api-config';
import { useAuth } from '../auth/hooks/useAuth';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import { Button, InputField } from '@shared/components';
import { useSelector } from 'react-redux';
import { getCourierId } from '@state/courierSlice';
import DeliveryRequestCard from '../components/delivery/DeliveryHistoryCard';
import GoogleMapPicker, { LocationData } from '../components/delivery/GoogleMapPicker';
import { useDeliveryAvailability } from '../hooks/useDeliveryAvailability';

interface DeliveryRequestForm {
  recipientName: string;
  contactNumber: string;
  deliveryLocation: string;
  deliveryCoordinates?: { lat: number; lng: number };
  preferredTime: string;
  customTime?: string;
  type: string;
  notes: string;
  amount: number;
  status: string;
  zone: string;
  packages: string[];
  customerId?: number;
  courierId?: number;
  deliveryLocationId?: number; // This is actually townId
  townId?: number;
  parishId?: number;
  customerName?: string;
  date: string;
  paymentMethod?: string;
  referenceNumber?: string;
  // New fields for location selection
  knutsfordLocation?: number;
  zipMailLocation?: number;
}

interface ExtendedTown {
  id?: number;
  name?: string;
  parishId?: number;
  parish?: {
    id?: number;
    name?: string;
  };
  geo?: {
    lat?: number;
    lng?: number;
  };
  deliveryFee: number;
  [key: string]: any;
}

// Haversine formula to calculate distance between two points in meters
const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371000; // Earth's radius in meters
  const toRadians = (degrees: number): number => degrees * (Math.PI / 180);

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Default zone radius in meters (10km - generous for town coverage)
const ZONE_RADIUS_METERS = 10000;

const DeliveryRequest = () => {
  const navigate = useNavigate();
  const { user, courier } = useAuth();
  const { isDeliveryAvailable, isLoading: isDeliveryConfigLoading } = useDeliveryAvailability();
  const [view, setView] = useState<'request' | 'history'>('request');

  // Redirect if delivery is not available (only after loading completes)
  useEffect(() => {
    if (!isDeliveryConfigLoading && !isDeliveryAvailable) {
      navigate('/packages');
    }
  }, [isDeliveryAvailable, isDeliveryConfigLoading, navigate]);
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);
  const [selectedPackages, setSelectedPackages] = useState<number[]>([]);
  const [recipientName, setRecipientName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchPackage, setSearchPackage] = useState('');
  const [hasChangedDate, setHasChangedDate] = useState(false);
  const [parishes, setParishes] = useState<Parish[]>([]);
  const courierId = useSelector(getCourierId);
  const locationApi = new LocationApi(apiConfig());
  const townApi = new TownApi(apiConfig());
  const parishApi = new ParishApi(apiConfig());
  const courierPackageApi = new CourierPackageApi(apiConfig());
  const deliveryApi = new DeliveryRequestApi(apiConfig());
  const deliveryFeeApi = new DeliveryFeeApi(apiConfig());
  const [deliveryFees, setDeliveryFees] = useState<DeliveryFee[]>([]);
  const [formData, setFormData] = useState<DeliveryRequestForm>({
  recipientName: '',
  contactNumber: '',
  deliveryLocation: '',
  deliveryCoordinates: undefined,
  preferredTime: 'Anytime',
  customTime: '',
  type: '',
  notes: '',
  amount: 0,
  status: 'pending',
  zone: '',
  packages: [],
  customerId: undefined,
  courierId: courierId,
  deliveryLocationId: undefined,
  townId: undefined,
  parishId: undefined,
  customerName: '',
  date: new Date().toISOString().split('T')[0],
  paymentMethod: 'cash-on-delivery',
  referenceNumber: '',
  // New fields for location selection
  knutsfordLocation: undefined,
  zipMailLocation: undefined
});

  // Data states
  const [readyPackages, setReadyPackages] = useState<CourierPackage[]>([]);
  const [deliveryHistory, setDeliveryHistory] = useState<DeliveryRequestModel[]>([]);
  const [previouslyRequestedPackageIds, setpreviouslyRequestedPackageIds] = useState<Set<string>>(new Set())
  const [locations, setLocations] = useState<Location[]>([]);
  const [towns, setTowns] = useState<ExtendedTown[]>([]);
  const [deliveryConfig, setDeliveryConfig] = useState<DeliveryConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [locationOutsideZone, setLocationOutsideZone] = useState<{
    isOutside: boolean;
    distance?: number;
    zoneName?: string;
  } | null>(null);
  // The zone circle is guidance, not a fence: customers whose address sits
  // outside it can confirm and carry on rather than being stuck.
  const [acceptedOutsideZone, setAcceptedOutsideZone] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState<number | null>(null);

  useEffect(() => {
    const loadInitialData = async () => {
      if (courierId) {
        await fetchDeliveryConfig();
        await fetchParishes();
        await fetchLocations();
        await fetchTowns()
      }
    };

    loadInitialData();
  }, [courierId]);

  useEffect(() => {
    if (formData.type && formData.type.toLowerCase() === 'courier') {
      setFormData(prev => ({
        ...prev,
        amount: calculateAmount()
      }));
    }
  }, [formData.zone, formData.type]);

  // Dynamic delivery types based on courier configuration
  const getDeliveryTypes = useCallback(() => {
    if (!courier || !deliveryConfig) return [];

    const types = [];

    // Only include courier's own delivery service if inHouseActive is true
    if (deliveryConfig.inHouseActive) {
      types.push({
        id: 'courier',
        name: `${courier.name || 'Courier'} Delivery`,
        description: 'Standard delivery service'
      });
    }

    // Add Knutsford Express if active
    if (deliveryConfig.knutsfordActive) {
      types.push({
        id: 'knutsford',
        name: 'Knutsford Express',
        description: 'Express delivery service'
      });
    }

    // Add ZipMail if active
    if (deliveryConfig.zipMailActive) {
      types.push({
        id: 'zipmail',
        name: 'ZipMail',
        description: 'Fast mail delivery service'
      });
    }

    return types;
  }, [courier, deliveryConfig]);

  const preferredTimes = [
    { id: 'anytime', name: 'Anytime' },
    { id: 'morning', name: 'Morning (8AM - 12PM)' },
    { id: 'afternoon', name: 'Afternoon (12PM - 4PM)' },
    { id: 'evening', name: 'Evening (4PM - 8PM)' }
  ];

  // Get today's date in YYYY-MM-DD format for date input min attribute
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get minimum allowed delivery date (matching mobile app behavior)
  const getMinDeliveryDate = () => {
    const today = new Date();
    if (isSameDayDeliveryAllowed()) {
      return today.toISOString().split('T')[0];
    }
    // If after 10am, minimum is tomorrow
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  // Check if selected date is today
  const isToday = (dateString: string) => {
    if (!dateString) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Parse the date string as local time (YYYY-MM-DD format)
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);
    selectedDate.setHours(0, 0, 0, 0);
    
    return today.getTime() === selectedDate.getTime();
  };

  // Check if selected date is in the past
  const isPastDate = (dateString: string) => {
    if (!dateString) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Parse the date string as local time (YYYY-MM-DD format)
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day);
    selectedDate.setHours(0, 0, 0, 0);
    
    return selectedDate.getTime() < today.getTime();
  };

  // Check if same-day delivery is allowed (before 10am cutoff)
  const isSameDayDeliveryAllowed = () => {
    const now = new Date();
    const cutoffHour = 10; // 10am cutoff (matching mobile app)
    return now.getHours() < cutoffHour;
  };

  // Check if same-day delivery is being requested after 10am
  const isSameDayDeliveryAfterCutoff = (dateString: string) => {
    if (!isToday(dateString)) {
      return false; // Not same-day delivery
    }
    return !isSameDayDeliveryAllowed();
  };

  // Step validation functions
  const canProceedToStep2 = () => {
    return selectedPackages.length > 0;
  };

  // An out-of-zone address only blocks until the customer confirms it.
  const isZoneBlocked = () =>
    Boolean(locationOutsideZone?.isOutside) && !acceptedOutsideZone;

  const canProceedToStep3 = () => {
    if (isZoneBlocked()) {
      return false;
    }
    return formData.type && formData.zone && formData.deliveryLocation;
  };

  const canSubmitForm = () => {
    if (isZoneBlocked()) {
      return false;
    }
    return (
      selectedPackages.length > 0 &&
      formData.type &&
      formData.zone &&
      formData.deliveryLocation &&
      recipientName.trim() &&
      contactNumber.trim() &&
      deliveryDate &&
      preferredTime &&
      !isPastDate(deliveryDate) &&
      !isSameDayDeliveryAfterCutoff(deliveryDate)
    );
  };

  // Step navigation functions
  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep((prev) => (prev + 1) as 1 | 2 | 3);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as 1 | 2 | 3);
    }
  };

      // Get Knutsford locations
  const getKnutsfordLocations = () => {
    console.log('Current locations state:', locations);
    
    // Debug: Log all unique type values
    const uniqueTypes = [...new Set(locations.map(loc => loc.type))];
    console.log('All unique location types:', uniqueTypes);
    
    // Debug: Log first few locations to see their structure
    console.log('First 5 locations:', locations.slice(0, 5));
    
    const knutsfordLocations = locations.filter(location => {
      const typeMatch = location.type?.toLowerCase() === 'knutsford';
      const nameMatch = location.name?.toLowerCase().includes('knutsford');
      console.log(`Location ${location.name}: type="${location.type}", typeMatch=${typeMatch}, nameMatch=${nameMatch}, hasGeo=${!!location.geo}, geo=${JSON.stringify(location.geo)}`);
      return typeMatch || nameMatch;
    });
    console.log('Filtered Knutsford locations:', knutsfordLocations);
    
    // Debug: Log location IDs to ensure they are valid numbers
    knutsfordLocations.forEach(loc => {
      console.log(`Knutsford location "${loc.name}" has ID: ${loc.id} (type: ${typeof loc.id})`);
    });
    
    return knutsfordLocations;
  };

  const fetchParishes = async () => {
    try {
      // Only fetch if we don't already have parishes loaded
      if (parishes.length === 0) {
        const response = await parishApi.parishFind({
          filter: JSON.stringify({
            order: 'name ASC'
          })
        });
        setParishes(response);
      }
      return parishes;
    } catch (error) {
      console.error('Error fetching parishes:', error);
      return [];
    }
  };

  const fetchDeliveryFees = async () => {
    try {
      const response = await deliveryFeeApi.deliveryFeeFind({
        filter: JSON.stringify({ where: { courierId } })
      });
      setDeliveryFees(response);
    } catch (error) {
      console.error('Error fetching delivery fees:', error);
      return [];
    }
  };

  const fetchTowns = async () => {
    try {
      // First fetch delivery fees for this courier
      const feesResponse = await deliveryFeeApi.deliveryFeeFind({
        filter: JSON.stringify({
          where: { courierId },
        }),
      });

      if (feesResponse.length === 0) {
        setTowns([]);
        setDeliveryFees([]);
        return [];
      }

      // Get town IDs from delivery fees
      const townIds = feesResponse
        .map((fee) => fee.townId)
        .filter((id): id is number => id !== undefined && id !== null);

      if (townIds.length === 0) {
        setTowns([]);
        setDeliveryFees(feesResponse);
        return [];
      }

      // Fetch towns by IDs with parish data
      const townsResponse = await townApi.townFind({
        filter: JSON.stringify({
          where: { id: { inq: townIds } },
          include: ['parish'],
        }),
      });

      // Map towns with their delivery fees
      const extendedTowns: ExtendedTown[] = townsResponse
        .map((town) => {
          const fee = feesResponse.find((f) => f.townId === town.id);
          return {
            ...town,
            parish: town.parish,
            deliveryFee: fee?.amount || 0,
          };
        })
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      setTowns(extendedTowns);
      setDeliveryFees(feesResponse);
      return extendedTowns;
    } catch (error) {
      console.error('Error fetching towns:', error);
      return [];
    }
  };

  const isFormValid = () => {
    // Step 1 validation: Customer and packages must be selected
    if (currentStep === 1) {
      return formData.customerId && formData.packages.length > 0;
    }

    // Step 2 validation: All required fields must be filled
    if (currentStep === 2) {
      const requiredFields = [
        formData.type, // Delivery type is always required
        formData.recipientName,
        formData.contactNumber,
        formData.date,
        formData.deliveryLocation
      ];

      // Zone is only required for courier delivery
      if (formData.type === 'courier') {
        requiredFields.push(formData.zone);
      }

      // Knutsford location is required for Knutsford delivery
      if (formData.type === 'knutsford') {
        if (!formData.knutsfordLocation) {
          return false;
        }
      }

      // ZipMail location is required for ZipMail delivery
      if (formData.type === 'zipmail') {
        if (!formData.zipMailLocation) {
          return false;
        }
      }

      // Custom time is required if custom time is selected
      if (formData.preferredTime === 'custom' && !formData.customTime) {
        return false;
      }

      return requiredFields.every(field => field && field.toString().trim() !== '');
    }

    return false;
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const type = e.target.value;
    setFormData(prev => ({
      ...prev,
      type,
      // Keep delivery location and coordinates when switching types
      deliveryLocation: '', 
      deliveryCoordinates: undefined,
      deliveryLocationId: undefined,
      townId: undefined,
      parishId: undefined,
      zone: '', // Clear zone when switching delivery types
      amount: 0,
      // Clear location selection fields when switching types
      knutsfordLocation: undefined,
      zipMailLocation: undefined
    }));

    setLocationOutsideZone(null);
    setAcceptedOutsideZone(false);

    isFormValid();

    if (type === 'courier') {
      fetchTowns(); // Fetch all available towns for this courier
    } else if (type && type !== 'custom') {
      fetchLocations();
    }
  };

    // Get ZipMail locations
  const getZipMailLocations = () => {
    console.log('Current locations state for ZipMail:', locations);
    
    // Debug: Log all unique type values
    const uniqueTypes = [...new Set(locations.map(loc => loc.type))];
    console.log('All unique location types for ZipMail:', uniqueTypes);
    
    const zipMailLocations = locations.filter(location => {
      const typeMatch = location.type?.toLowerCase() === 'zipmail';
      const nameMatch = location.name?.toLowerCase().includes('zipmail') || location.name?.toLowerCase().includes('zip mail');
      console.log(`Location ${location.name}: type="${location.type}", typeMatch=${typeMatch}, nameMatch=${nameMatch}`);
      return typeMatch || nameMatch;
    });
    console.log('Filtered ZipMail locations:', zipMailLocations);
    
    // Debug: Log location IDs to ensure they are valid numbers
    zipMailLocations.forEach(loc => {
      console.log(`ZipMail location "${loc.name}" has ID: ${loc.id} (type: ${typeof loc.id})`);
    });
    
    return zipMailLocations;
  };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // For date field, ensure we don't allow past dates
    if (name === 'date') {
      // For date input, value comes in YYYY-MM-DD format
      const [year, month, day] = value.split('-').map(Number);
      const selectedDate = new Date(year, month - 1, day); // month is 0-based
      selectedDate.setHours(0, 0, 0, 0);
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
      
      if (selectedDate < today) {
        showErrorToast({
          title: 'Invalid Date',
          message: 'Please select a future date'
        });
        return;
      }

      // Store the date as YYYY-MM-DD format (no timezone conversion)
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      return;
    }

    // Handle zone change for courier delivery
    if (name === 'zone' && formData.type === 'courier') {
      const townId = parseInt(value);

      setFormData(prev => ({
        ...prev,
        zone: value,
        townId: townId || undefined,
        deliveryLocationId: townId || undefined,
        amount: calculateAmount()
      }));

      // A new zone means the previous confirmation no longer applies.
      setAcceptedOutsideZone(false);
      setLocationOutsideZone(
        evaluateZone(formData.deliveryCoordinates, townId || undefined)
      );
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

    const calculateAmount = () => {
    if (formData.type && formData.type.toLowerCase() === 'courier' && formData.zone) {
      const townId = parseInt(formData.zone);
      const selectedTown = towns.find(town => town.id === townId);
      const baseFee = selectedTown?.deliveryFee || 0;
      return baseFee; // Only return the delivery fee, not package prices
    }
    return 0;
  };

  // The selected town's centre and radius, drawn on the map so the customer
  // can see the area the courier normally covers.
  const zoneRestriction = useMemo(() => {
    if (formData.type !== 'courier' || !formData.zone) return undefined;
    const town = towns.find(t => t.id === parseInt(formData.zone));
    if (!town?.geo?.lat || !town?.geo?.lng) return undefined;
    return {
      center: { lat: town.geo.lat, lng: town.geo.lng },
      radius: ZONE_RADIUS_METERS
    };
  }, [formData.type, formData.zone, towns]);

  // How far an address sits from its zone centre. Returns null when it is
  // inside the zone, or when there is nothing to compare against.
  const evaluateZone = useCallback((
    coordinates: { lat: number; lng: number } | undefined,
    townId?: number
  ) => {
    if (!coordinates || !townId) return null;
    const town = towns.find(t => t.id === townId);
    if (!town?.geo?.lat || !town?.geo?.lng) return null;

    const distance = calculateDistance(
      town.geo.lat,
      town.geo.lng,
      coordinates.lat,
      coordinates.lng
    );

    return distance > ZONE_RADIUS_METERS
      ? { isOutside: true, distance, zoneName: town.name }
      : null;
  }, [towns]);

  // A pin dropped (or address searched) on the map.
  const handleMapLocationChange = useCallback((location: LocationData) => {
    setFormData(prev => ({
      ...prev,
      deliveryLocation: location.address,
      deliveryCoordinates: location.coordinates
    }));

    // Every new pin is a fresh decision — a previous confirmation should not
    // silently cover a different address.
    setAcceptedOutsideZone(false);
    setLocationOutsideZone(
      evaluateZone(location.coordinates, formData.townId)
    );
  }, [evaluateZone, formData.townId]);

  // Step indicator component
  const StepIndicator = () => (
    <div className="mb-6">
      <div className="flex items-center justify-center space-x-4">
        <div className={`flex items-center ${currentStep >= 1 ? 'text-primary' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStep >= 1 ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white'}`}>
            1
          </div>
          <span className="ml-2 text-sm font-medium">Packages</span>
        </div>
        <div className={`w-12 h-0.5 ${currentStep >= 2 ? 'bg-primary' : 'bg-gray-300'}`}></div>
        <div className={`flex items-center ${currentStep >= 2 ? 'text-primary' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStep >= 2 ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white'}`}>
            2
          </div>
          <span className="ml-2 text-sm font-medium">Delivery Details</span>
        </div>
        <div className={`w-12 h-0.5 ${currentStep >= 3 ? 'bg-primary' : 'bg-gray-300'}`}></div>
        <div className={`flex items-center ${currentStep >= 3 ? 'text-primary' : 'text-gray-400'}`}>
          <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${currentStep >= 3 ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white'}`}>
            3
          </div>
          <span className="ml-2 text-sm font-medium">Contact Info</span>
        </div>
      </div>
    </div>
  );

  // Set default delivery date based on time (today if before 10am, tomorrow otherwise)
  // Matching mobile app behavior
  useEffect(() => {
    setDeliveryDate(getMinDeliveryDate());

    // Set default recipient name from user
    if (user?.firstName && user?.lastName) {
      setRecipientName(`${user.firstName} ${user.lastName}`);
    }
  }, [user]);

  // Fetch courier's delivery configuration
  const fetchDeliveryConfig = useCallback(async () => {
    if (!courier?.id) return;

    try {
      const configApi = new DeliveryConfigApi(apiConfig());

      const filter = {
        where: {
          courierId: courier.id
        }
      };

      const response = await configApi.deliveryConfigFind({
        filter: JSON.stringify(filter)
      });

      if (response.length > 0) {
        setDeliveryConfig(response[0]);
      }
    } catch (err: any) {
      console.error('Error fetching delivery config:', err);
      // Don't show error toast as this is not critical for the user
    }
  }, [courier?.id]);

  // Fetch ready packages
  const fetchReadyPackages = useCallback(async () => {
    if (!user?.id || !courier?.id) return;

    setIsLoading(true);
    setError('');

    try {
      const packageApi = new CourierPackageApi(apiConfig());

      const filter = {
        where: {
          and: [
            { ownerId: user.id },
            { courierId: courier.id },
            { status: { inq: ['At Warehouse', 'In Transit', 'Ready'] } }
          ]
        },
        include: ['invoice'],
        order: ['createdOn DESC']
      };

      const response = await packageApi.courierPackageFind({
        filter: JSON.stringify(filter)
      });

      setReadyPackages(response);
    } catch (err: any) {
      console.error('Error fetching ready packages:', err);
      setError('Failed to load packages');
      showErrorToast({
        title: 'Error',
        message: 'Failed to load packages ready for delivery'
      });
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, courier?.id]);

  // Fetch delivery history
  const fetchDeliveryHistory = useCallback(async () => {
    if (!user?.id || !courier?.id) return;

    try {
      const deliveryApi = new DeliveryRequestApi(apiConfig());

      const filter = {
        where: {
          and: [
            { customerId: user.id },
            { courierId: courier.id }
          ]
        },
        order: ['createdAt DESC']
      };

      const response = await deliveryApi.deliveryRequestFind({
        filter: JSON.stringify(filter)
      });
      setDeliveryHistory(response);
      // Extract package IDs from active delivery requests (exclude only delivered/completed/cancelled)
      const packageIds: Set<string> = new Set();
      response.forEach(request => {
        const status = request.status?.toLowerCase();
        // Show warning for all statuses except completed ones
        const isCompleted = status === 'delivered' || status === 'completed' || status === 'cancelled';
        if (!isCompleted && request.packages) {
          request.packages.forEach(packageId => {
            packageIds.add(packageId.toString());
          });
        }
      });
      setpreviouslyRequestedPackageIds(packageIds);
    } catch (err: any) {
      console.error('Error fetching delivery history:', err);
      showErrorToast({
        title: 'Error',
        message: 'Failed to load delivery history'
      });
    }
  }, [user?.id, courier?.id]);

  // Fetch locations based on delivery type
  const fetchLocations = useCallback(async () => {
        try {
      // Fetch regular locations - fetch all locations and filter in code
      const locationResponse = await locationApi.locationFind({
        filter: JSON.stringify({
          // Don't filter by type here, we'll filter in the code
        })
      });
      console.log('Regular locations fetched:', locationResponse);
      
      // Debug: Check if locations have geo data
      locationResponse.forEach((location, index) => {
        console.log(`Location ${index}:`, {
          id: location.id,
          name: location.name,
          type: location.type,
          hasGeo: !!location.geo,
          geo: location.geo
        });
      });
      
      // Fetch delivery fees
      const deliveryFeesResponse = await deliveryFeeApi.deliveryFeeFind({
        filter: JSON.stringify({
          where: { courierId }
        })
      });
      console.log('Delivery fees fetched:', deliveryFeesResponse);
      
      // Fetch towns for the delivery fees
      const townIds = deliveryFeesResponse.map(fee => fee.townId).filter(id => id !== undefined);
      const townsResponse = await townApi.townFind({
        filter: JSON.stringify({
          where: {
            id: { inq: townIds }
          },
          include: ['parish']
        })
      });
      console.log('Towns fetched:', townsResponse);
      
      // Process delivery fees into locations format
      const feeLocations = deliveryFeesResponse.map(fee => {
        const town = townsResponse.find(t => t.id === fee.townId);
        return {
          id: town?.id,
          name: town?.name,
          type: 'knutsford', // Mark these as Knutsford locations
          geo: town?.geo ? {
            lat: town.geo.lat || 0,
            lng: town.geo.lng || 0
          } : undefined,
          amount: fee.amount
        };
      }).filter(loc => loc.id && loc.name); // Filter out any invalid entries
      
      console.log('Processed fee locations:', feeLocations);
      
      // Combine both location sources
      const allLocations = [...locationResponse, ...feeLocations];
      console.log('All combined locations:', allLocations);
      
      setLocations(allLocations);
      return allLocations;
    } catch (error) {
      console.error('Error fetching all locations:', error);
      return [];
    }
  }, [formData.type]);

  // Load data when component mounts
  useEffect(() => {
    fetchReadyPackages();
    fetchDeliveryHistory();
    fetchDeliveryConfig();
  }, [fetchReadyPackages, fetchDeliveryHistory, fetchDeliveryConfig]);

  const togglePackageSelection = (id: number) => {
    if (selectedPackages.includes(id)) {
      setSelectedPackages(selectedPackages.filter(pkgId => pkgId !== id));
    } else {
      setSelectedPackages([...selectedPackages, id]);
    }
  };

  const handleSelectAll = () => {
    const availablePackageIds = readyPackages.map(pkg => pkg.id || 0).filter(id => id !== 0);
    if (selectedPackages.length === availablePackageIds.length) {
      // If all are selected, deselect all
      setSelectedPackages([]);
    } else {
      // Otherwise select all available packages
      setSelectedPackages(availablePackageIds);
    }
  };

  // Calculate total value of selected packages
  const calculateTotalValue = () => {
    return readyPackages
      .filter(pkg => pkg.id && selectedPackages.includes(pkg.id))
      .reduce((total, pkg) => {
        return total + (pkg.value || 0);
      }, 0);
  };

  // Filter packages based on search term
  const filteredPackages = readyPackages.filter(pkg =>
    pkg.trackingNo?.toLowerCase().includes(searchPackage.toLowerCase()) ||
    pkg.description?.toLowerCase().includes(searchPackage.toLowerCase()) ||
    pkg.merchant?.toLowerCase().includes(searchPackage.toLowerCase())
  );

    const handleKnutsfordLocationChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const value = e.target.value;
    console.log('Knutsford location change - raw value:', value, 'type:', typeof value);
    
    // Handle empty selection
    if (!value || value === '') {
      console.log('Empty Knutsford location selected, clearing form data');
      setFormData(prev => ({
        ...prev,
        knutsfordLocation: undefined,
        deliveryLocation: '',
        deliveryCoordinates: undefined,
        zone: '',
        amount: 0,
        deliveryLocationId: undefined
      }));
      return;
    }
    
    const locationId = parseInt(value);
    console.log('Parsed location ID:', locationId, 'type:', typeof locationId);
    
    // Handle invalid location ID
    if (isNaN(locationId)) {
      console.error('Invalid Knutsford location ID:', value);
      return;
    }
    
    const knutsfordLocation = getKnutsfordLocations().find(loc => loc.id === locationId);
    console.log('Found Knutsford location:', knutsfordLocation);
    
    if (!knutsfordLocation) {
      console.error('Knutsford location not found for ID:', locationId);
      console.log('Available Knutsford locations:', getKnutsfordLocations().map(loc => ({ id: loc.id, name: loc.name })));
      return;
    }
    
    console.log('Knutsford location selected:', knutsfordLocation);
    
    // Knutsford deliveries are free (customer pays at pickup)
    const fee = 0;
    
    setFormData(prev => ({
      ...prev,
      knutsfordLocation: locationId,
      zone: knutsfordLocation.name || '',
      deliveryLocation: knutsfordLocation.name || '',
      deliveryCoordinates: knutsfordLocation.geo ? {
        lat: knutsfordLocation.geo.lat || 0,
        lng: knutsfordLocation.geo.lng || 0
      } : undefined,
      amount: fee,
      deliveryLocationId: locationId
    }));
    
    console.log('Form data updated with coordinates:', {
      deliveryLocation: knutsfordLocation.name,
      deliveryCoordinates: knutsfordLocation.geo ? {
        lat: knutsfordLocation.geo.lat || 0,
        lng: knutsfordLocation.geo.lng || 0
      } : undefined
    });
  };

  const isPackageAlreadyRequested = (id?: number) => {
    if (!id) return false;

    // If we're editing a request, exclude packages from that request
    if (editingRequestId) {
      const editingRequest = deliveryHistory.find(r => r.id === editingRequestId);
      if (editingRequest?.packages?.includes(id.toString())) {
        return false; // Package is part of the request being edited
      }
    }

    return previouslyRequestedPackageIds?.has(id.toString());
  };

  // Get status color for package status badges (matching mobile app)
  const getStatusColor = (status?: string) => {
    switch (status?.toLowerCase()) {
      case 'ready':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
      case 'at warehouse':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30';
      case 'in transit':
        return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/30';
      case 'delivered':
      case 'completed':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30';
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/30';
    }
  };

  // Get selected package objects (matching mobile app)
  const selectedPackageObjects = readyPackages.filter(
    pkg => pkg.id && selectedPackages.includes(pkg.id)
  );

  // Separate packages by readiness status (matching mobile app)
  const notReadyPackages = selectedPackageObjects.filter(
    pkg => pkg.status !== 'Ready'
  );
  const allPackagesReady = notReadyPackages.length === 0;
  const readyPackagesCount = selectedPackageObjects.length - notReadyPackages.length;

  // Separate packages by invoice payment status (matching mobile app)
  const paidPackages = selectedPackageObjects.filter(
    pkg => (pkg as any).invoice?.status?.toLowerCase() === 'paid'
  );
  const unpaidPackages = selectedPackageObjects.filter(
    pkg => (pkg as any).invoice && (pkg as any).invoice?.status?.toLowerCase() !== 'paid'
  );

  // Calculate values using invoice amounts (matching mobile app)
  const paidValue = paidPackages.reduce((total, pkg) => total + ((pkg as any).invoice?.amount || 0), 0);
  const unpaidValue = unpaidPackages.reduce((total, pkg) => total + ((pkg as any).invoice?.amount || 0), 0);

      // Lookup helpers for package info by ID (assuming packageId is string or number)
    const getPackageById = (packageId: string) => {
      return readyPackages.find(pkg => pkg.id?.toString() === packageId.toString());
    };

    const getPackageTrackingNumber = (packageId: string) => {
      const pkg = getPackageById(packageId);
      return pkg?.trackingNo || 'N/A';
    };

    const getPackageWeight = (packageId: string) => {
      const pkg = getPackageById(packageId);
      return pkg?.weight || 0; // Replace with actual weight property name if different
    };

    const getPackageDescription = (packageId: string) => {
      const pkg = getPackageById(packageId);
      return pkg?.description || '-';
    };

    const getPackageMerchant = (packageId: string) => {
      const pkg = getPackageById(packageId);
      return pkg?.merchant || '-';
    };

    const getPackageValue = (packageId: string) => {
      const pkg = getPackageById(packageId);
      return pkg?.value || 0;
    };

  const cancelDelivery = async (requestId?: number) => {
    if (!requestId) return;
    if (!window.confirm('Are you sure you want to cancel this delivery request?')) return;

    try {
      setIsLoading(true);
      await deliveryApi.deliveryRequestDeleteById({
        id: requestId.toString()
      });
      showSuccessToast({ title: 'Success', message: 'Delivery request cancelled' });
      // Refresh delivery history after cancel
      fetchDeliveryHistory();
    } catch (err) {
      console.error('Error cancelling delivery request:', err);
      showErrorToast({ title: 'Error', message: 'Failed to cancel delivery request' });
    } finally {
      setIsLoading(false);
    }
  };

  // Edit existing delivery request (only for pending)
  const editDelivery = (requestId?: number) => {
    if (!requestId) return;

    const request = deliveryHistory.find(r => r.id === requestId);
    if (!request) {
      showErrorToast({ title: 'Error', message: 'Delivery request not found' });
      return;
    }

    // Only allow editing pending requests
    if (request.status?.toLowerCase() !== 'pending') {
      showErrorToast({ title: 'Error', message: 'Only pending delivery requests can be edited' });
      return;
    }

    // Determine location IDs based on delivery type
    const deliveryType = request.type?.toLowerCase();
    const locationId = request.deliveryLocationId;

    // Populate form with request data
    setFormData({
      recipientName: request.recipientName || '',
      contactNumber: request.contactNumber || '',
      deliveryLocation: request.deliveryLocation || '',
      deliveryCoordinates: request.deliveryCoordinates,
      preferredTime: request.preferredTime || 'Anytime',
      customTime: '',
      type: request.type || '',
      notes: request.notes || '',
      amount: request.amount || 0,
      status: request.status || 'pending',
      zone: request.zone || '',
      packages: request.packages || [],
      customerId: request.customerId,
      courierId: request.courierId,
      deliveryLocationId: request.deliveryLocationId,
      townId: deliveryType === 'courier' ? locationId : undefined,
      parishId: undefined,
      customerName: '',
      date: request.date ? new Date(request.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      paymentMethod: request.paymentMethod || 'cash-on-delivery',
      referenceNumber: request.referenceNumber || '',
      knutsfordLocation: deliveryType === 'knutsford' ? locationId : undefined,
      zipMailLocation: deliveryType === 'zipmail' ? locationId : undefined
    });

    // Set individual state fields
    setRecipientName(request.recipientName || '');
    setContactNumber(request.contactNumber || '');
    setDeliveryDate(request.date ? new Date(request.date).toISOString().split('T')[0] : getMinDeliveryDate());
    setPreferredTime(request.preferredTime || '');
    setNotes(request.notes || '');

    // Set selected packages
    if (request.packages && request.packages.length > 0) {
      setSelectedPackages(request.packages.map(id => parseInt(id.toString())));
    }

    // Set editing request ID to track that we're editing
    setEditingRequestId(requestId);

    // Switch to request view starting at step 1
    setCurrentStep(1);
    setView('request');

    showSuccessToast({
      title: 'Editing Request',
      message: 'Make your changes and submit to update the delivery request'
    });
  };

    // Handle ZipMail location selection
  const handleZipMailLocationChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const value = e.target.value;
    console.log('ZipMail location change - raw value:', value, 'type:', typeof value);
    
    // Handle empty selection
    if (!value || value === '') {
      console.log('Empty ZipMail location selected, clearing form data');
      setFormData(prev => ({
        ...prev,
        zipMailLocation: undefined,
        deliveryLocation: '',
        zone: '',
        deliveryCoordinates: undefined,
        amount: 0,
        deliveryLocationId: undefined
      }));
      return;
    }
    
    const locationId = parseInt(value);
    console.log('Parsed location ID:', locationId, 'type:', typeof locationId);
    
    // Handle invalid location ID
    if (isNaN(locationId)) {
      console.error('Invalid ZipMail location ID:', value);
      return;
    }
    
    const zipMailLocation = getZipMailLocations().find(loc => loc.id === locationId);
    console.log('Found ZipMail location:', zipMailLocation);
    
    if (!zipMailLocation) {
      console.error('ZipMail location not found for ID:', locationId);
      console.log('Available ZipMail locations:', getZipMailLocations().map(loc => ({ id: loc.id, name: loc.name })));
      return;
    }
    
    console.log('ZipMail location selected:', zipMailLocation);
    
    // ZipMail deliveries are free (customer pays at pickup)
    const fee = 0;
    
    setFormData(prev => ({
      ...prev,
      zipMailLocation: locationId,
      deliveryLocation: zipMailLocation.name || '',
      zone: zipMailLocation.name || '',
      deliveryCoordinates: zipMailLocation.geo ? {
        lat: zipMailLocation.geo.lat || 0,
        lng: zipMailLocation.geo.lng || 0
      } : undefined,
      amount: fee,
      deliveryLocationId: locationId
    }));
    
    console.log('Form data updated with coordinates:', {
      deliveryLocation: zipMailLocation.name,
      deliveryCoordinates: zipMailLocation.geo ? {
        lat: zipMailLocation.geo.lat || 0,
        lng: zipMailLocation.geo.lng || 0
      } : undefined
    });
  };

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setFormData({
      recipientName: '',
      contactNumber: '',
      deliveryLocation: '',
      deliveryCoordinates: undefined,
      preferredTime: 'Anytime',
      customTime: '',
      type: '',
      notes: '',
      amount: 0,
      status: 'pending',
      zone: '',
      packages: [],
      customerId: undefined,
      courierId: courierId,
      deliveryLocationId: undefined,
      townId: undefined,
      parishId: undefined,
      customerName: '',
      date: new Date().toISOString().split('T')[0],
      paymentMethod: 'cash-on-delivery',
      referenceNumber: '',
      knutsfordLocation: undefined,
      zipMailLocation: undefined
    });
    setSelectedPackages([]);
    setRecipientName('');
    setContactNumber('');
    setDeliveryDate(getMinDeliveryDate());
    setPreferredTime('');
    setNotes('');
    setCurrentStep(1);
    setLocationOutsideZone(null);
    setAcceptedOutsideZone(false);
    setEditingRequestId(null);
  }, [courierId, getMinDeliveryDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();


      // Check if any selected package is already requested
    const hasAlreadyRequested = selectedPackages.some(pkgId => isPackageAlreadyRequested(pkgId));
    if (hasAlreadyRequested) {
      showErrorToast({
        title: 'Duplicate Package',
        message: 'One or more selected packages have already been requested for delivery. Please deselect them to continue. or cancel the previous request',
        duration: 5000
      });
      return; // Prevent submission
    }

    // Validate form
    if (selectedPackages.length === 0) {
      showErrorToast({
        title: 'Error',
        message: 'Please select at least one package'
      });
      return;
    }
    if (!deliveryConfig) {
      showErrorToast({
        title: 'Error',
        message: 'Delivery configuration not available. Please try again later.'
      });
      return;
    }
    if (getDeliveryTypes().length === 0) {
      showErrorToast({
        title: 'Error',
        message: 'Delivery services are currently unavailable for this courier'
      });
      return;
    }
    if (!formData.type) {
      showErrorToast({
        title: 'Error',
        message: 'Please select a delivery type'
      });
      return;
    }
    if (!formData.zone) {
      showErrorToast({
        title: 'Error',
        message: 'Please select a delivery zone'
      });
      return;
    }
    if (!location) {
      showErrorToast({
        title: 'Error',
        message: 'Please select a delivery location'
      });
      return;
    }
    if (!recipientName) {
      showErrorToast({
        title: 'Error',
        message: 'Please enter recipient name'
      });
      return;
    }
    if (!contactNumber) {
      showErrorToast({
        title: 'Error',
        message: 'Please enter contact number'
      });
      return;
    }
    if (!deliveryDate) {
      showErrorToast({
        title: 'Error',
        message: 'Please select delivery date'
      });
      return;
    }
    if (isPastDate(deliveryDate)) {
      showErrorToast({
        title: 'Error',
        message: 'Delivery date cannot be in the past. Please select today or a future date.'
      });
      return;
    }
    if (isSameDayDeliveryAfterCutoff(deliveryDate)) {
      showErrorToast({
        title: 'Same-Day Delivery Unavailable',
        message: 'Same-day deliveries cannot be requested after 10:00 AM on the same day. Please select a future date for your delivery.'
      });
      return;
    }
    if (!preferredTime) {
      showErrorToast({
        title: 'Error',
        message: 'Please select preferred time'
      });
      return;
    }

    if (!user?.id || !courier?.id) {
      showErrorToast({
        title: 'Error',
        message: 'User or courier information not available'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Parse delivery date as local time
      const [year, month, day] = deliveryDate.split('-').map(Number);
      const deliveryDateObject = new Date(year, month - 1, day);
      
      // Calculate totals - only include unpaid invoice amounts
      const selectedPkgs = readyPackages.filter(pkg => pkg.id && selectedPackages.includes(pkg.id));
      const unpaidPkgs = selectedPkgs.filter(pkg => (pkg as any).invoice && (pkg as any).invoice?.status?.toLowerCase() !== 'paid');
      const packageTotal = unpaidPkgs.reduce((total, pkg) => total + ((pkg as any).invoice?.amount || 0), 0);
      const deliveryFee = formData.amount;
      const grandTotal = packageTotal + deliveryFee;

      // Prepare delivery request data
      const deliveryData: NewDeliveryRequest = {
        type: formData.type,
        recipientName: recipientName.trim(),
        contactNumber: contactNumber.trim(),
        date: deliveryDateObject,
        preferredTime: preferredTime,
        deliveryLocation: formData.deliveryLocation,
        deliveryCoordinates: formData.deliveryCoordinates,
        zone: formData.zone,
        status: 'Pending',
        amount: deliveryFee,
        packageTotal: packageTotal,
        grandTotal: grandTotal,
        notes: notes.trim() || undefined,
        packages: selectedPackages.map(id => id.toString()),
        courierId: courier.id,
        customerId: user.id,
        paymentMethod: 'cash-on-delivery',
        referenceNumber: editingRequestId ? formData.referenceNumber : `DR-${Date.now()}`,
        deliveryLocationId: formData.deliveryLocationId || undefined
      };

      let resultRequest;

      if (editingRequestId) {
        // Update existing delivery request
        await deliveryApi.deliveryRequestPrototypePatchAttributes({
          id: editingRequestId.toString(),
          data: deliveryData
        });
        resultRequest = { id: editingRequestId };
      } else {
        // Create new delivery request
        resultRequest = await deliveryApi.deliveryRequestCreate({
          data: deliveryData
        });
      }

      if (resultRequest.id) {
        showSuccessToast({
          title: 'Success',
          message: editingRequestId ? 'Delivery request updated successfully!' : 'Delivery request submitted successfully!'
        });
        // Reset form and switch to history view
        resetForm();
        await fetchDeliveryHistory();
        setView('history');
      } else {
        throw new Error('Failed to create delivery request');
      }

    } catch (error: any) {
      console.error('Error creating delivery request:', error);
      const errorMessage = error.response?.data?.error?.message ||
                          error.message ||
                          'Failed to submit delivery request. Please try again.';

      showErrorToast({
        title: 'Error',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="bg-gray-50 dark:bg-gray-900 min-h-screen pb-24 md:pb-6">
      <div className="max-w-4xl mx-auto p-4">
        {/* Request/History Tabs */}
        <div className="flex mb-6 bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm">
          <button className={`flex-1 py-3 px-4 text-center font-medium ${view === 'request' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setView('request')}>
            <RefreshCw className="h-5 w-5 inline-block mr-2" />
            Request
          </button>
          <button className={`flex-1 py-3 px-4 text-center font-medium ${view === 'history' ? 'bg-primary text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'}`} onClick={() => setView('history')}>
            <Clock className="h-5 w-5 inline-block mr-2" />
            History
          </button>
        </div>

        {view === 'request' ? <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            {/* Header */}
            <div className="p-6 text-center border-b border-gray-200 dark:border-gray-700">
              <div className="mx-auto bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <RefreshCw className="h-6 w-6" />
              </div>
              <h1 className="text-xl font-bold dark:text-white">
                Request a Delivery
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Speedy Local Delivery Service
              </p>
            </div>

            {/* Delivery Services Unavailable Message */}
            {deliveryConfig && getDeliveryTypes().length === 0 && (
              <div className="mx-6 mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <Info className="h-5 w-5 text-red-400 dark:text-red-300" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                      Delivery Services Temporarily Unavailable
                    </h3>
                    <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                      <p>
                        We're sorry, but delivery services are currently unavailable for {courier?.name || 'this courier'}.
                        This may be due to temporary service interruptions or maintenance.
                      </p>
                      <p className="mt-2">
                        Please check back later or contact customer support for assistance.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="p-6">
              {/* Editing Indicator */}
              {editingRequestId && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                    <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Editing Delivery Request #{editingRequestId}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setView('history');
                    }}
                    className="text-sm text-yellow-700 dark:text-yellow-300 hover:text-yellow-900 dark:hover:text-yellow-100 underline"
                  >
                    Cancel Editing
                  </button>
                </div>
              )}

              {/* Step Indicator */}
              <StepIndicator />

              {/* Step 1: Package Selection */}
              {currentStep === 1 && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Select Packages for Delivery
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Choose the packages you'd like to have delivered
                    </p>
                  </div>

                  {/* Package search */}
                  <div className="relative mb-4">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search packages..."
                      value={searchPackage}
                      onChange={e => setSearchPackage(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    />
                  </div>

                  {/* Select All Button */}
                  <div className="flex justify-between items-center mb-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {readyPackages.length} package{readyPackages.length !== 1 ? 's' : ''} available
                    </div>
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className="text-sm text-primary dark:text-primary border border-primary/30 rounded-md px-3 py-1 hover:bg-primary/10 dark:hover:bg-primary/20"
                    >
                      {selectedPackages.length === readyPackages.length ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>

                  {/* Selected packages summary (matching mobile app) */}
                  {selectedPackages.length > 0 && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg p-4 mb-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <CheckCircle className="h-5 w-5 text-primary dark:text-primary mr-2" />
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                            {selectedPackages.length} package{selectedPackages.length !== 1 ? 's' : ''} selected
                          </span>
                        </div>
                        <div className="flex flex-col items-end">
                          {paidPackages.length > 0 && (
                            <span className="text-xs font-medium text-green-600 dark:text-green-400">
                              ${paidValue.toFixed(2)} paid
                            </span>
                          )}
                          {unpaidPackages.length > 0 && (
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              ${unpaidValue.toFixed(2)} unpaid
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Package Readiness Warning (matching mobile app) */}
                  {selectedPackages.length > 0 && !allPackagesReady && (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                      <div className="flex items-start gap-2 mb-2">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                        <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                          Not All Packages Are Ready
                        </h4>
                      </div>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                        {readyPackagesCount} of {selectedPackages.length} package{selectedPackages.length !== 1 ? 's are' : ' is'} ready for delivery.
                        Your delivery request will be processed once all {notReadyPackages.length} remaining package{notReadyPackages.length !== 1 ? 's become' : ' becomes'} available.
                      </p>
                      <div className="space-y-2">
                        {notReadyPackages.slice(0, 3).map((pkg) => (
                          <div key={pkg.id} className="flex justify-between items-center">
                            <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                              {pkg.trackingNo}
                            </span>
                            <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${getStatusColor(pkg.status)}`}>
                              {pkg.status}
                            </span>
                          </div>
                        ))}
                        {notReadyPackages.length > 3 && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                            +{notReadyPackages.length - 3} more package{notReadyPackages.length - 3 !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Package grid */}
                  <div className="bg-gray-50 dark:bg-gray-900 rounded-lg">
                    {filteredPackages.length === 0 ? (
                      <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                        <PackageIcon className="h-12 w-12 mx-auto mb-3 opacity-40" />
                        <p>No packages available for delivery</p>
                        {searchPackage && (
                          <p className="text-sm mt-1">Try a different search term</p>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4">
                        {filteredPackages.map(pkg => {
                          const isSelected = selectedPackages.includes(pkg.id || 0);
                          const hasExistingRequest = isPackageAlreadyRequested(pkg.id);
                          const pkgInvoice = (pkg as any).invoice;
                          const isPaid = pkgInvoice?.status?.toLowerCase() === 'paid';

                          return (
                            <div
                              key={pkg.id}
                              className={`relative rounded-lg border cursor-pointer ${
                                hasExistingRequest
                                  ? 'border-yellow-400 dark:border-yellow-600 border-2'
                                  : isSelected
                                    ? 'border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20 shadow-sm border-2'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-sm'
                              } transition-all`}
                              onClick={() => togglePackageSelection(pkg.id || 0)}
                            >
                              <div className="p-3">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex items-center">
                                    <div className={`rounded-full p-2 mr-2 ${
                                      hasExistingRequest
                                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                                        : isSelected
                                          ? 'bg-blue-100 dark:bg-blue-900/50 text-primary dark:text-primary'
                                          : 'bg-orange-100 dark:bg-orange-900/30 text-orange-500 dark:text-orange-400'
                                    }`}>
                                      <PackageIcon className="h-4 w-4" />
                                    </div>
                                    <div>
                                      <h3 className="font-medium text-gray-900 dark:text-white text-sm">
                                        {pkg.trackingNo}
                                      </h3>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {pkg.merchant}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex-shrink-0">
                                    <div className={`w-5 h-5 rounded-full border ${
                                      isSelected
                                        ? 'border-primary bg-primary'
                                        : 'border-gray-300 dark:border-gray-600'
                                    } flex items-center justify-center`}>
                                      {isSelected && (
                                        <CheckCircle className="h-3 w-3 text-white" />
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  <p className="truncate">{pkg.description}</p>
                                  <div className='flex justify-between items-center mt-2'>
                                    {/* Invoice amount and status */}
                                    <div className="flex items-center gap-2">
                                      {pkgInvoice ? (
                                        <>
                                          <p className={`font-medium ${isPaid ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>
                                            ${(pkgInvoice.amount || 0).toFixed(2)}
                                          </p>
                                          {isPaid && (
                                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded">
                                              PAID
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        <p className="text-gray-500 dark:text-gray-400">No invoice</p>
                                      )}
                                    </div>

                                    {/* Status badge and warning */}
                                    <div className="flex items-center gap-2">
                                      {/* Package Status Badge */}
                                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${getStatusColor(pkg.status)}`}>
                                        {pkg.status || 'Unknown'}
                                      </span>

                                      {/* Existing Request Warning */}
                                      {hasExistingRequest && (
                                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded text-[10px] font-medium">
                                          <AlertTriangle className="w-3 h-3" />
                                          Requested
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex justify-end mt-6">
                    <Button
                      type="button"
                      onClick={nextStep}
                      disabled={!canProceedToStep2()}
                      className="px-6"
                    >
                      Next: Delivery Details
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2: Delivery Details */}
              {currentStep === 2 && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Delivery Details
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Choose your delivery type, zone, and location
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Delivery Type */}
                    <div>
                      <InputField
                        label="Delivery Type"
                        type="select"
                        name="deliveryType"
                        value={formData.type}
                        onChange={handleTypeChange}
                        options={getDeliveryTypes().map(type => ({
                          value: type.id,
                          label: type.name
                        }))}
                        required
                        fullWidth
                      />
                    </div>

                    {/* Rest of the form only shows if delivery type is selected */}
                    {formData.type && (
                      <>
                        {/* Zone Selection for courier Delivery */}
                        {formData.type === 'courier' && (
                          <div>
                            <InputField
                              label="Delivery Zone"
                              name="zone"
                              type="select"
                              value={formData.zone}
                        onChange={handleChange}
                        options={[
                                ...towns.map(town => ({
                                  value: town.id?.toString() || '',
                                  label: `${town.name}${town.parish?.name ? `, ${town.parish.name}` : ''} - $${(town.deliveryFee || 0).toFixed(2)}`
                                }))
                        ]}
                        fullWidth
                        required
                      />
                      </div>)}
                          {/* Knutsford Location Selection */}
                          {formData.type === 'knutsford' && (
                            <div>
                              <InputField
                                label="Knutsford Location"
                                name="knutsfordLocation"
                                type="select"
                                value={formData.knutsfordLocation?.toString() || ''}
                                onChange={handleKnutsfordLocationChange}
                                options={[
                                  { value: '', label: "Choose Knutsford Location" },
                                  ...getKnutsfordLocations().map(location => ({
                                    value: location.id?.toString() || '',
                                    label: location.name || ''
                                  }))
                                ]}
                                fullWidth
                                required
                              />
                              
                            </div>
                          )}

                          {/* ZipMail Location Selection */}
                          {formData.type === 'zipmail' && (
                            <div>
                              <InputField
                                label="ZipMail Location"
                                name="zipMailLocation"
                                type="select"
                                value={formData.zipMailLocation?.toString() || ''}
                                onChange={handleZipMailLocationChange}
                                options={[
                                  { value: '', label: "Choose ZipMail Location" },
                                  ...getZipMailLocations().map(location => ({
                                    value: location.id?.toString() || '',
                                    label: location.name || ''
                                  }))
                                ]}
                                fullWidth
                                required
                              />
                              
                            </div>
                          )}

                          {/* Delivery Location - Only shown for courier delivery type */}
                          {formData.type === 'courier' && (
                            <div className="space-y-4">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Delivery Location
                              </label>

                              {/* Map picker — search, drop a pin, or use current location */}
                              <GoogleMapPicker
                                value={formData.deliveryLocation && formData.deliveryCoordinates ? {
                                  address: formData.deliveryLocation,
                                  coordinates: formData.deliveryCoordinates
                                } : undefined}
                                onChange={handleMapLocationChange}
                                zoneRestriction={zoneRestriction}
                                placeholder="Search for your delivery address..."
                                height="260px"
                                fullWidth
                              />

                              {/* Advisory when the pin falls outside the selected zone */}
                              {formData.deliveryCoordinates && locationOutsideZone?.isOutside && (
                                <div className={`p-4 rounded-lg border ${
                                  acceptedOutsideZone
                                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                                }`}>
                                  <div className="flex items-start gap-3">
                                    <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                                      acceptedOutsideZone
                                        ? 'text-amber-600 dark:text-amber-400'
                                        : 'text-red-600 dark:text-red-400'
                                    }`} />
                                    <div className="flex-1">
                                      <h4 className={`text-sm font-semibold mb-1 ${
                                        acceptedOutsideZone
                                          ? 'text-amber-800 dark:text-amber-200'
                                          : 'text-red-800 dark:text-red-200'
                                      }`}>
                                        Location Outside Delivery Zone
                                      </h4>
                                      <p className={`text-sm mb-2 ${
                                        acceptedOutsideZone
                                          ? 'text-amber-700 dark:text-amber-300'
                                          : 'text-red-700 dark:text-red-300'
                                      }`}>
                                        The pin is approximately{' '}
                                        <strong>{((locationOutsideZone.distance || 0) / 1000).toFixed(1)}km</strong> from{' '}
                                        <strong>{locationOutsideZone.zoneName}</strong>.
                                      </p>
                                      <p className={`text-xs mb-3 ${
                                        acceptedOutsideZone
                                          ? 'text-amber-600 dark:text-amber-400'
                                          : 'text-red-600 dark:text-red-400'
                                      }`}>
                                        Pick the zone that covers your area for the right delivery fee, or
                                        continue with this address — {courier?.name || 'the courier'} may contact you
                                        to confirm the trip and adjust the fee.
                                      </p>
                                      <label
                                        htmlFor="accept-outside-zone"
                                        className={`flex items-center gap-2 text-sm font-medium cursor-pointer ${
                                          acceptedOutsideZone
                                            ? 'text-amber-800 dark:text-amber-200'
                                            : 'text-red-800 dark:text-red-200'
                                        }`}
                                      >
                                        <input
                                          id="accept-outside-zone"
                                          type="checkbox"
                                          checked={acceptedOutsideZone}
                                          onChange={(e) => setAcceptedOutsideZone(e.target.checked)}
                                          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                        />
                                        Deliver to this address anyway
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Divider */}
                              <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                                — or enter address manually —
                              </div>

                              {/* Manual Address Input */}
                              <InputField
                                label=""
                                type="text"
                                name="deliveryLocation"
                                value={formData.deliveryLocation}
                                onChange={(e) => {
                                  setFormData(prev => ({
                                    ...prev,
                                    deliveryLocation: e.target.value,
                                    // Clear coordinates if user manually edits the address
                                    deliveryCoordinates: undefined
                                  }));
                                  // Clear zone warning when manually editing address
                                  setLocationOutsideZone(null);
                                  setAcceptedOutsideZone(false);
                                }}
                                placeholder="Enter specific delivery address"
                                fullWidth
                              />

                              {/* Directions / Notes for Driver */}
                              <InputField
                                label="Directions for Driver (Optional)"
                                type="textarea"
                                name="notes"
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="E.g., Blue gate on the left, second floor apartment..."
                                fullWidth
                              />
                            </div>
                          )}

                          {/* Additional Notes for Knutsford/ZipMail */}
                          {(formData.type === 'knutsford' || formData.type === 'zipmail') && (
                            <InputField
                              label="Additional Notes (Optional)"
                              type="textarea"
                              name="notes"
                              value={notes}
                              onChange={(e) => setNotes(e.target.value)}
                              placeholder="Any special instructions..."
                              fullWidth
                            />
                          )}

                          {/* Delivery Fee Summary - Show when courier delivery type and zone is selected */}
                          {formData.type === 'courier' && formData.zone && (
                            <div className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg mt-4">
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-700 dark:text-gray-300">Delivery Fee</span>
                                <span className="text-base font-semibold text-green-600 dark:text-green-400">
                                  ${formData.amount.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          )}
                      </>
                    )}
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex justify-between mt-6">
                    <Button
                      type="button"
                      onClick={prevStep}
                      variant="outline"
                      className="px-6"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={nextStep}
                      disabled={!canProceedToStep3()}
                      className="px-6"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
              {/* Step 3: Contact Information */}
              {currentStep === 3 && (
                <div>
                  <div className="mb-6">
                    <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      Contact Information
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Provide recipient details and delivery preferences
                    </p>
                  </div>

                  <div className="space-y-6">
                    {/* Recipient Name */}
                    <div>
                      <InputField
                        label="Recipient Name"
                        type="text"
                        name="recipientName"
                        value={recipientName}
                        onChange={(e) => setRecipientName(e.target.value)}
                        placeholder="Enter recipient name"
                        required
                        fullWidth
                      />
                    </div>

                    {/* Contact Number */}
                    <div>
                      <InputField
                        label="Contact Number"
                        type="phone"
                        name="contactNumber"
                        value={contactNumber}
                        onChange={(e) => setContactNumber(e.target.value)}
                        placeholder="876-123-4567"
                        required
                        fullWidth
                      />
                    </div>

                    {/* Delivery Date */}
                    <div>
                      <InputField
                        label="Delivery Date"
                        type="date"
                        name="deliveryDate"
                        value={deliveryDate}
                        onChange={(e) => {
                          setDeliveryDate(e.target.value);
                          setHasChangedDate(true);
                        }}
                        required
                        fullWidth
                        min={getMinDeliveryDate()}
                      />
                      {/* Same-day delivery cutoff info (matching mobile app) */}
                      {!isPastDate(deliveryDate) && !isSameDayDeliveryAfterCutoff(deliveryDate) && (
                        <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                          {isSameDayDeliveryAllowed()
                            ? 'Same-day delivery available (requests must be made before 10am)'
                            : 'Same-day delivery cutoff has passed. Earliest delivery: tomorrow'}
                        </p>
                      )}
                      {hasChangedDate && isPastDate(deliveryDate) && (
                        <p className="text-red-500 dark:text-red-400 text-xs mt-1">
                          Delivery date cannot be in the past. Please select today or a future date.
                        </p>
                      )}
                      {deliveryDate && isSameDayDeliveryAfterCutoff(deliveryDate) && (
                        <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                          <div className="flex items-start">
                            <svg className="w-5 h-5 text-red-500 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                                Same-Day Delivery Unavailable
                              </p>
                              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                                Same-day deliveries cannot be requested after 10:00 AM on the same day. Please select a future date for your delivery.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Preferred Time */}
                    <div>
                      <InputField
                        label="Preferred Time"
                        type="select"
                        name="preferredTime"
                        value={preferredTime}
                        onChange={(e) => setPreferredTime(e.target.value)}
                        options={preferredTimes.map(time => ({
                          value: time.id,
                          label: time.name
                        }))}
                        required
                        fullWidth
                      />
                    </div>

                    {/* Payment Method - Static display since only cash on delivery is available */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Payment Method
                      </label>
                      <div className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white">
                        Cash on Delivery
                      </div>
                    </div>

                    {/* Order Summary */}
                    <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">
                        Order Summary
                      </h3>

                      {/* Show paid packages if any */}
                      {paidPackages.length > 0 && (
                        <div className="flex justify-between items-center py-2">
                          <span className="text-sm text-green-600 dark:text-green-400">
                            Already Paid ({paidPackages.length} package{paidPackages.length !== 1 ? 's' : ''})
                          </span>
                          <span className="text-sm font-medium text-green-600 dark:text-green-400">
                            ${paidValue.toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Show unpaid packages */}
                      {unpaidPackages.length > 0 && (
                        <div className="flex justify-between items-center py-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Unpaid ({unpaidPackages.length} package{unpaidPackages.length !== 1 ? 's' : ''})
                          </span>
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            ${unpaidValue.toFixed(2)}
                          </span>
                        </div>
                      )}

                      {/* Delivery Fee */}
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Delivery Fee
                        </span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          ${formData.amount.toFixed(2)}
                        </span>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

                      {/* Amount Due */}
                      <div className="flex justify-between items-center py-2">
                        <span className="text-base font-semibold text-gray-900 dark:text-white">
                          Amount Due
                        </span>
                        <span className="text-lg font-bold text-primary">
                          ${(unpaidValue + formData.amount).toFixed(2)}
                        </span>
                      </div>

                      {/* Payment Method */}
                      <div className="flex justify-between items-center py-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Payment
                        </span>
                        <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                          Cash on Delivery
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex justify-between mt-6">
                    <Button
                      type="button"
                      onClick={prevStep}
                      variant="outline"
                      className="px-6"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={!canSubmitForm() || isSubmitting}
                      isLoading={isSubmitting}
                      className="px-6"
                    >
                      {editingRequestId ? 'Update Request' : 'Submit Request'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </div> :
          // History View
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-lg font-medium mb-4 dark:text-white">
              Delivery History
            </h2>
            {deliveryHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2">No delivery history found</h3>
                <p className="text-gray-600 dark:text-gray-400">Your previous deliveries will appear here once completed.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {deliveryHistory.map(request => (
                 <DeliveryRequestCard
                  key={request.id}
                  request={request}
                  getPackageTrackingNumber={getPackageTrackingNumber}
                  getPackageWeight={getPackageWeight}
                  getPackageDescription={getPackageDescription}
                  getPackageMerchant={getPackageMerchant}
                  getPackageValue={getPackageValue}
                  onEditDelivery={editDelivery}
                  onCancelDelivery={cancelDelivery}
                />

                ))}
              </div>
            )}
          </div>
        }
      </div>
    </div>;
};
export default DeliveryRequest;
